import { useEffect, useRef, useState } from "react";
import {
  Container,
  Title,
  Text,
  Box,
  Stack,
  Group,
  ThemeIcon,
  Badge,
  useComputedColorScheme,
} from "@mantine/core";
import {
  IconLicense,
  IconRosette,
  IconBuildingCommunity,
  IconChecklist,
  IconShieldCheck,
} from "@tabler/icons-react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { AnimateIn } from "./AnimateIn";
import { LifecycleScene } from "./jewel/LifecycleScene";

/**
 * The rule-library lifecycle section. Leads the page with the platform's true
 * differentiator — the regulatory distribution engine — and animates its full
 * lifecycle: Author → Publish → Adopt → Enforce → Prove. A 3D gem (the encoded
 * rule bundle) pulses and propagates outward to orbiting facility nodes as the
 * lifecycle auto-advances, with the copy highlighting the active stage.
 */

const STAGES = [
  {
    icon: IconLicense,
    label: "Author",
    title: "We encode your state's rules",
    description:
      "Your state's compliance requirements — IL CILA, TX HCS, and growing — are built into a maintained, versioned rule bundle by our team. The actual regulations, not a blank form builder you configure yourself.",
    color: "#12b886",
  },
  {
    icon: IconRosette,
    label: "Publish",
    title: "The bundle is published",
    description:
      "Each bundle is versioned and published as a single source of truth for the state. One authoritative definition of compliant — for every operator, every facility.",
    color: "#0ca678",
  },
  {
    icon: IconBuildingCommunity,
    label: "Adopt",
    title: "Your facilities adopt it in one action",
    description:
      "Adopting a bundle fans every requirement out to every active resident across every facility — and each new admission inherits it automatically. No spreadsheet, no per-resident setup, no facility-by-facility drift.",
    color: "#4a8c71",
  },
  {
    icon: IconChecklist,
    label: "Enforce",
    title: "Compliance runs itself",
    description:
      "Daily sweeps check every requirement, gate medical appointments on missing forms, and flag anything overdue — automatically. The platform produces compliance instead of just storing it.",
    color: "#15aabf",
  },
  {
    icon: IconShieldCheck,
    label: "Prove",
    title: "Audit-ready, on any date",
    description:
      "When a state changes a rule, we publish an update and it propagates everywhere. And on survey day, prove exactly how compliant you were on any day a surveyor asks — reconstructed, not guessed.",
    color: "#7950f2",
  },
];

const STAGE_MS = 3200;

export function JewelSection() {
  const computedColorScheme = useComputedColorScheme("light");
  const reduceMotion = useReducedMotion();
  const sectionRef = useRef<HTMLDivElement>(null);
  // Run the lifecycle only while the section is near view — deliberately not
  // `once`, so the loop stops when the reader scrolls past instead of animating
  // (and re-rendering) off-screen forever.
  const inView = useInView(sectionRef, { margin: "200px" });

  // Lifecycle state machine — auto-advances through the stages and loops.
  const [stage, setStage] = useState(0);
  useEffect(() => {
    // Reduced motion: hold on the final "Prove" stage, fully built up.
    if (reduceMotion) {
      setStage(STAGES.length - 1);
      return;
    }
    if (!inView) return;
    const id = window.setInterval(() => {
      setStage((s) => (s + 1) % STAGES.length);
    }, STAGE_MS);
    return () => window.clearInterval(id);
  }, [inView, reduceMotion]);

  const dark = computedColorScheme === "dark";

  return (
    <Box
      component="section"
      id="compliance-engine"
      ref={sectionRef}
      py={100}
      style={{
        position: "relative",
        overflow: "hidden",
        background: dark
          ? "linear-gradient(180deg, #1A1B1E 0%, #11261f 50%, #1A1B1E 100%)"
          : "linear-gradient(180deg, #ffffff 0%, #effaf4 50%, #ffffff 100%)",
      }}
    >
      <Container size="xl">
        <AnimateIn>
          <Stack align="center" gap="sm" mb={40}>
            <Badge
              size="lg"
              radius="sm"
              variant="light"
              color="accentGreen"
              leftSection={<IconShieldCheck size={16} />}
            >
              The Rule Library Lifecycle
            </Badge>
            <Title order={2} size="2.5rem" fw={700} ta="center" maw={820}>
              Your State's Rules,{" "}
              <Text
                component="span"
                inherit
                style={{
                  background:
                    "linear-gradient(135deg, #12b886 0%, #0ca678 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Done For You
              </Text>
            </Title>
            <Text size="lg" c="dimmed" ta="center" maw={720}>
              Compliance isn't a form builder — it's knowing exactly what your
              state requires and making sure it actually happens, in every
              facility, for every resident. Here's the full lifecycle, end to
              end.
            </Text>
          </Stack>
        </AnimateIn>

        {/* Stage rail — shows the whole lifecycle at a glance, lights up the
            active stage, and lets the viewer jump to any stage.
            `overflowX: auto` makes this a scroll container on both axes (CSS
            forces a `visible` overflow to compute as `auto` when its partner
            isn't), so it clips vertically too. The vertical padding is the room
            the active stage needs to grow into — its `scale: 1.12` and 6px
            ring were being sheared off, cutting the label in half. */}
        <Box mb={26} style={{ overflowX: "auto", padding: "14px 0" }}>
          <Group justify="center" gap={0} wrap="nowrap" miw={680}>
            {STAGES.map((s, i) => {
              const Icon = s.icon;
              const isActive = i === stage;
              const isPast = i < stage;
              return (
                <Group key={s.label} gap={0} wrap="nowrap">
                  <Box
                    onClick={() => setStage(i)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") setStage(i);
                    }}
                    style={{ cursor: "pointer", textAlign: "center" }}
                  >
                    <motion.div
                      animate={{
                        scale: isActive ? 1.12 : 1,
                        opacity: isActive || isPast ? 1 : 0.5,
                      }}
                      transition={{ duration: 0.3 }}
                    >
                      <ThemeIcon
                        size={52}
                        radius="xl"
                        variant={isActive ? "filled" : "light"}
                        style={{
                          backgroundColor: isActive ? s.color : `${s.color}1a`,
                          color: isActive ? "#fff" : s.color,
                          boxShadow: isActive
                            ? `0 0 0 6px ${s.color}22`
                            : "none",
                          transition: "all 0.3s ease",
                        }}
                      >
                        <Icon size={26} stroke={1.6} />
                      </ThemeIcon>
                      <Text
                        size="xs"
                        fw={isActive ? 700 : 500}
                        mt={6}
                        c={isActive ? undefined : "dimmed"}
                      >
                        {s.label}
                      </Text>
                    </motion.div>
                  </Box>
                  {i < STAGES.length - 1 && (
                    <Box
                      style={{
                        width: 56,
                        height: 3,
                        marginTop: -18,
                        borderRadius: 2,
                        background: dark ? "#2C2E33" : "#e9ecef",
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      <motion.div
                        initial={false}
                        animate={{ width: i < stage ? "100%" : "0%" }}
                        transition={{ duration: 0.4 }}
                        style={{
                          position: "absolute",
                          inset: 0,
                          background:
                            "linear-gradient(90deg, #12b886, #0ca678)",
                          borderRadius: 2,
                        }}
                      />
                    </Box>
                  )}
                </Group>
              );
            })}
          </Group>
        </Box>

        {/* The illustrated lifecycle storyboard: state rule set → group
            home(s) + residents → survey passed. */}
        <Box
          style={{
            position: "relative",
            borderRadius: 24,
            padding: "1rem",
            background:
              "radial-gradient(circle at 50% 40%, rgba(18,184,134,0.10) 0%, transparent 70%)",
          }}
        >
          <LifecycleScene stage={stage} />
        </Box>

        {/* Active-stage caption — cross-fades as the lifecycle advances.
            Every stage is rendered into the same grid cell so the box is always
            as tall as its longest caption at the current width. A fixed
            `minHeight` can't do that: the descriptions wrap to different line
            counts on narrow screens, so the box grew and shrank on each tick
            and pushed the rest of the page around. */}
        <Box style={{ maxWidth: 720, margin: "32px auto 0", display: "grid" }}>
          {STAGES.map((s, i) => {
            const isActive = i === stage;
            return (
              <motion.div
                key={s.label}
                initial={false}
                animate={{ opacity: isActive ? 1 : 0, y: isActive ? 0 : 16 }}
                // Delay the incoming caption so the outgoing one has faded
                // first — the out-then-in feel of the old `mode="wait"`.
                transition={{ duration: 0.35, delay: isActive ? 0.15 : 0 }}
                aria-hidden={!isActive}
                style={{
                  gridArea: "1 / 1",
                  pointerEvents: isActive ? undefined : "none",
                }}
              >
                <Group
                  gap="md"
                  mb="sm"
                  wrap="nowrap"
                  align="center"
                  justify="center"
                >
                  <ThemeIcon
                    size={56}
                    radius="md"
                    variant="light"
                    style={{
                      backgroundColor: `${s.color}1a`,
                      color: s.color,
                    }}
                  >
                    <s.icon size={28} stroke={1.5} />
                  </ThemeIcon>
                  <Box>
                    <Text
                      size="xs"
                      fw={700}
                      style={{ letterSpacing: "1.5px", color: s.color }}
                    >
                      STEP {i + 1} · {s.label.toUpperCase()}
                    </Text>
                    <Title order={3} size="h3" fw={700}>
                      {s.title}
                    </Title>
                  </Box>
                </Group>
                <Text
                  size="md"
                  c="dimmed"
                  ta="center"
                  style={{ lineHeight: 1.7 }}
                >
                  {s.description}
                </Text>
              </motion.div>
            );
          })}
        </Box>
      </Container>
    </Box>
  );
}
